/**
 * /clients route — searchable client list page.
 *
 * Design decisions:
 * - validateSearch uses Zod inline schema with .optional().catch(undefined) on q
 * - Permission check is done in ClientsListPage (canView gate) so that a user
 *   without client.view lands on /clients and sees a "permission denied" message
 *   rather than being silently redirected — this makes the denied state testable.
 * - component is a thin wrapper rendering ClientsListPage
 */
import * as React from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ClientsListPage } from '@ooh/screens';
import { PageTitleContext } from '../__root';

// ─── Search schema ────────────────────────────────────────────

const ClientsSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
});

// ─── Route definition ─────────────────────────────────────────

export const Route = createFileRoute('/clients/')({
  validateSearch: ClientsSearchSchema,
  // No beforeLoad redirect: permission check is handled in ClientsListPage so that
  // users without client.view see a permission-denied message at /clients rather than
  // being silently redirected away.
  component: ClientsPage,
});

// ─── Page component ───────────────────────────────────────────

function ClientsPage() {
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t } = useTranslation('clients');

  React.useEffect(() => {
    setPageTitle(t('pageTitle'));
  }, [setPageTitle, t]);

  const search = useSearch({ from: '/clients/' });
  const navigate = useNavigate({ from: '/clients/' });

  return (
    <ClientsListPage
      search={search}
      onSearchChange={(patch) =>
        void navigate({ replace: true, search: (prev) => ({ ...prev, ...patch }) })
      }
    />
  );
}
